import { useEffect, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils.ts';

interface SliderProps {
  min: number;
  max: number;
  value: number;
  onChange: (value: number) => void;
  className?: string;
}

export function Slider({
  min,
  max,
  value,
  onChange,
  className = '',
}: SliderProps) {
  const isDraggingRef = useRef(false);

  const sliderRef = useRef<HTMLDivElement>(null!);

  const getPercentage = (current: number, min: number, max: number) => {
    return ((current - min) / (max - min)) * 100;
  };

  const handleTouchDown = () => {
    isDraggingRef.current = true;
  };

  const handleTouchMove = useCallback(
    (event: TouchEvent | MouseEvent) => {
      event.stopImmediatePropagation();
      if (!isDraggingRef.current || !sliderRef.current) return;
      const rect = sliderRef.current.getBoundingClientRect();
      const point = event instanceof MouseEvent ? event : event.touches[0];
      const percentage = (point.clientX - rect.left) / rect.width;
      const newValue = Math.round(percentage * (max - min) + min);
      onChange(Math.max(min, Math.min(max, newValue)));
    },
    [max, min, onChange]
  );

  useEffect(() => {
    const handleTouchUp = (event: TouchEvent | MouseEvent) => {
      event.stopImmediatePropagation();
      isDraggingRef.current = false;
    };

    sliderRef.current.addEventListener('touchmove', handleTouchMove);
    sliderRef.current.addEventListener('touchend', handleTouchUp);

    sliderRef.current.addEventListener('mousemove', handleTouchMove);
    sliderRef.current.addEventListener('mouseup', handleTouchUp);
    return () => {
      if (!sliderRef.current) return;

      sliderRef.current.removeEventListener('touchmove', handleTouchMove);
      sliderRef.current.removeEventListener('touchend', handleTouchUp);
      sliderRef.current.removeEventListener('mousemove', handleTouchMove);
      sliderRef.current.removeEventListener('mouseup', handleTouchUp);
    };
  }, [handleTouchMove]);

  return (
    <div
      ref={sliderRef}
      className={cn(
        `relative h-[6px] bg-white/30 rounded-full cursor-pointer text-[16px]`,
        className
      )}
      onTouchStart={handleTouchDown}
      onMouseDown={handleTouchDown}
    >
      <div
        className="absolute h-full bg-white rounded-full "
        style={{ width: `${getPercentage(value, min, max)}%` }}
      />
      <div
        className="absolute w-[12px] h-[12px] bg-white border-0 rounded-full -mt-[3px] -ml-2"
        style={{ left: `${getPercentage(value, min, max)}%` }}
      />
    </div>
  );
}
